import { useMemo } from 'react';
import { ExternalLink, FileText } from 'lucide-react';
import type { ApprovalAction } from '@/lib/types';

export interface DrivePreviewProps {
  payload: Record<string, unknown> | null | undefined;
  actions?: ApprovalAction[];
}

interface ResolvedSource {
  driveId: string | null;
  fallbackUrl: string | null;
}

const DRIVE_FILE_RE = /\/file\/d\/([^/?#]+)/;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function extractDriveIdFromUrl(url: string | null): string | null {
  if (!url) return null;
  const match = DRIVE_FILE_RE.exec(url);
  return match ? match[1] : null;
}

function actionDriveId(actions: ApprovalAction[] | undefined): string | null {
  if (!actions) return null;
  for (const action of [...actions].reverse()) {
    const payload = asRecord(action.payload);
    if (!payload) continue;
    const direct =
      asString(payload.drive_file_id) ??
      asString(payload.file_id) ??
      asString(payload.drive_id);
    if (direct) return direct;
    const moved = asRecord(payload.moved_file);
    const movedId =
      asString(moved?.drive_file_id) ??
      asString(moved?.file_id) ??
      asString(moved?.id);
    if (movedId) return movedId;
    const url =
      asString(payload.drive_view_url) ??
      asString(payload.source_file_url) ??
      asString(payload.preview_url);
    const fromUrl = extractDriveIdFromUrl(url);
    if (fromUrl) return fromUrl;
  }
  return null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function resolveDriveSource(
  payload: Record<string, unknown> | null | undefined,
  actions?: ApprovalAction[],
): ResolvedSource {
  if (!payload) return { driveId: actionDriveId(actions), fallbackUrl: null };

  const topAttachments = asArray(payload.attachments);
  const firstTop = topAttachments && topAttachments.length > 0 ? asRecord(topAttachments[0]) : null;

  const extra = asRecord(payload.extra);
  const extraAttachments = extra ? asArray(extra.attachments) : null;
  const firstExtra =
    extraAttachments && extraAttachments.length > 0 ? asRecord(extraAttachments[0]) : null;
  const defaultFields = asRecord(payload.default_fields);

  const driveId =
    asString(firstTop?.drive_id) ??
    asString(firstTop?.id) ??
    asString(firstExtra?.id) ??
    asString(defaultFields?.drive_file_id) ??
    asString(payload.drive_id) ??
    actionDriveId(actions) ??
    extractDriveIdFromUrl(asString(firstExtra?.preview_url)) ??
    extractDriveIdFromUrl(asString(payload.drive_view_url)) ??
    extractDriveIdFromUrl(asString(payload.source_file_url));

  const fallbackUrl =
    asString(payload.source_file_url) ??
    asString(payload.drive_view_url) ??
    asString(firstExtra?.preview_url) ??
    asString(firstTop?.url);

  return { driveId, fallbackUrl };
}

export function DrivePreview({ payload, actions }: DrivePreviewProps) {
  const { driveId, fallbackUrl } = useMemo(
    () => resolveDriveSource(payload, actions),
    [payload, actions],
  );

  if (driveId) {
    const viewUrl = `https://drive.google.com/file/d/${driveId}/view`;
    return (
      <DriveFileLink href={viewUrl} label="Open invoice scan" />
    );
  }

  if (fallbackUrl) {
    return <DriveFileLink href={fallbackUrl} label="Open invoice file" />;
  }

  return (
    <span className="inline-flex h-[40px] items-center gap-8 rounded-md border border-neutral-200 bg-neutral-50 px-12 text-sm text-neutral-500">
      <FileText className="h-16 w-16" aria-hidden="true" />
      No invoice scan
    </span>
  );
}

interface DriveFileLinkProps {
  href: string;
  label: string;
}

export function DriveFileLink({ href, label }: DriveFileLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-[40px] items-center justify-center gap-8 rounded-md border border-neutral-200 bg-neutral-0 px-12 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2"
    >
      <ExternalLink className="h-16 w-16" aria-hidden="true" />
      <span>{label}</span>
    </a>
  );
}
